/**
 * A ship, as a thing you can look at.
 *
 * Ships are authored flat and given depth when they exist in the world, so this
 * is where a design stops being a graph and becomes geometry: compartments are
 * extruded to the ship's depth, lines become the edges of that solid, and
 * components sit on top.
 *
 * Shared by the dock and — later — flight, so a ship looks the same wherever it
 * appears. The berth showed a plain box before this, which told you nothing
 * about what you had built.
 */

import {
  COMPONENT_SPECS,
  ROTATION_STEP,
  SHIP_DEPTH,
  boundsOf,
  componentOutline,
  drawScaleOf,
  COMPONENT_SCALE,
  renderFacing,
  modelFor,
  findCompartments,
  indexJoints,
  type ShipGraph,
} from '@interspacial/shared';
import * as THREE from 'three';

import { isSharedGeometry, loadComponentGeometry } from './models';
import { createStructureMaterial, withEdges } from './shaders/structure';

export interface ShipMesh {
  group: THREE.Group;
  /**
   * Hide the outward-facing plating.
   *
   * From a pilot seat the hull is *around* you, so the same walls that make a
   * ship read as solid from outside are the ones stopping you seeing anything.
   * Only the skin goes — internal bulkheads and the floor stay, so the ship
   * still reads as a place you are sitting inside rather than an open frame.
   */
  setSkinVisible: (visible: boolean) => void;
  /** Half the ship's longest side, for framing a camera on it. */
  radius: number;
  /**
   * How far the hull stands off whatever it is parked on.
   *
   * Landing gear is the one internal component that reaches *below* the hull,
   * so a ship with gear down rests on its feet rather than on its belly.
   */
  standoff: number;
  dispose: () => void;
}

/**
 * How far a `raised` component sits above the floor, in model units.
 *
 * Scaled with everything else, so it stays the same height relative to the part
 * it lifts however the component scale is tuned.
 */
const RAISED_HEIGHT = 1;

export interface ShipMeshOptions {
  /** The floor plating. Walls take their colour from the lines themselves. */
  hullColor?: number;
  /**
   * Cut the hull open so the inside is visible.
   *
   * Almost everything a ship carries lives inside it and is invisible in
   * flight, which makes a finished ship an opaque box — you cannot see the work
   * you did. Docked, the plating is drawn as an outline only so the compartments
   * and their contents read at a glance.
   */
  cutaway?: boolean;
  /**
   * Whether the ship is standing on its gear.
   *
   * Docked it is, so the legs are extended and holding the hull up; in flight
   * they are stowed and the ship sits on nothing.
   */
  landed?: boolean;
}

/**
 * Build a ship from its design.
 *
 * Everything wears the structure shader, so a ship reads the same way a station
 * does — black plate, lit edges — which is the point of having one shader for
 * everything built.
 */
export function buildShipMesh(graph: ShipGraph, options: ShipMeshOptions = {}): ShipMesh {
  const { hullColor = 0x9fc6e8, cutaway = false, landed = false } = options;
  const group = new THREE.Group();
  const joints = indexJoints(graph);
  const compartments = findCompartments(graph);

  /** Everything on the outside: hull walls and the roof. */
  const skin: THREE.Object3D[] = [];

  // A line is hull when fewer than two compartments claim it — the outside is
  // on the other side of it. Struts enclosing nothing count too.
  const claims = new Map<string, number>();
  for (const compartment of compartments) {
    for (const id of compartment.lineIds) claims.set(id, (claims.get(id) ?? 0) + 1);
  }
  const isHull = (lineId: string) => (claims.get(lineId) ?? 0) < 2;

  let radius = 0;
  for (const joint of graph.joints) {
    radius = Math.max(radius, Math.hypot(joint.x, joint.y));
  }

  /**
   * Compartments are floors; lines are walls.
   *
   * This used to extrude each compartment into a solid block, which drew every
   * wall in one colour and skipped the lines entirely — so a line you had paid
   * to repaint looked exactly like one you had not. Since the design already
   * says lines *are* the walls between compartments, drawing them as walls
   * fixes the paint and is the more faithful reading besides.
   */
  for (const compartment of compartments) {
    const points = compartment.jointIds
      .map((id) => joints.get(id))
      .filter((j): j is NonNullable<typeof j> => Boolean(j))
      .map((j) => new THREE.Vector2(j.x, j.y));
    if (points.length < 3) continue;

    // Floor and ceiling are flat quads, like the walls.
    //
    // The floor was an extruded slab, which put two coplanar surfaces a third
    // of a unit apart at the bottom of every hull and z-fought with the walls
    // standing on it. A hull is six panels around a space, not a tray with
    // walls glued on — so all six are the same kind of thing.
    const outline = new THREE.Shape(points);

    for (const face of ['floor', 'ceiling'] as const) {
      const plate = new THREE.ShapeGeometry(outline);

      // Normals decide what the cutaway keeps: a downward-facing surface is
      // read as floor and stays solid, while the ceiling faces up and fades,
      // which is what opens the hull without cutting anything.
      //
      // The normal is flipped directly rather than by rotating the geometry a
      // half turn. Rotating about X maps (x, y, z) to (x, −y, −z) — it mirrors
      // the plate in Y as well as turning it over, so the floor came out as a
      // reflection of the hull above it.
      if (face === 'floor') {
        const normals = plate.getAttribute('normal');
        for (let i = 0; i < normals.count; i++) {
          normals.setXYZ(i, -normals.getX(i), -normals.getY(i), -normals.getZ(i));
        }
        normals.needsUpdate = true;
      }
      plate.translate(0, 0, face === 'floor' ? -SHIP_DEPTH / 2 : SHIP_DEPTH / 2);

      const mesh = new THREE.Mesh(
        withEdges(plate),
        createStructureMaterial({
          color: hullColor,
          fadeDistance: 900,
          ...(cutaway ? { cutaway: { bottom: -SHIP_DEPTH / 2, top: SHIP_DEPTH / 2 } } : {}),
        }),
      );
      (mesh.material as THREE.Material).side = THREE.DoubleSide;
      // The roof is skin; the floor is not. Standing on nothing reads far worse
      // than having no ceiling.
      if (face === 'ceiling') skin.push(mesh);
      group.add(mesh);
    }
  }

  // Every line, in the colour it was painted. Full height, so they enclose the
  // compartments they bound — and struts enclosing nothing still stand, because
  // a half-built extension is part of the ship its owner is midway through.
  for (const line of graph.lines) {
    const a = joints.get(line.a);
    const b = joints.get(line.b);
    if (!a || !b) continue;

    const length = Math.hypot(b.x - a.x, b.y - a.y);
    if (length < 1e-6) continue;

    // A quad, not a box.
    //
    // A wall is a panel — giving it thickness meant every line came with four
    // edges instead of one and read as a girder laid on its side. A single
    // double-sided quad is also cheaper, and its four boundary edges are
    // exactly the outline the edge shader should be drawing.
    const panel = new THREE.PlaneGeometry(length, SHIP_DEPTH);
    panel.rotateX(Math.PI / 2);

    const wall = new THREE.Mesh(
      withEdges(panel),
      createStructureMaterial({
        color: new THREE.Color(line.color).getHex(),
        fadeDistance: 900,
        // Cut open, walls dissolve toward the top and the floor stays solid, so
        // you can see in without the ship becoming an outline. A clipping plane
        // would have sliced the components in half as well.
        ...(cutaway ? { cutaway: { bottom: -SHIP_DEPTH / 2, top: SHIP_DEPTH / 2 } } : {}),
      }),
    );
    // Visible from inside the hull as well as out, which a plane is not by
    // default — and a cutaway is looked at from inside more than outside.
    (wall.material as THREE.Material).side = THREE.DoubleSide;

    wall.position.set((a.x + b.x) / 2, (a.y + b.y) / 2, 0);
    wall.rotation.z = Math.atan2(b.y - a.y, b.x - a.x);
    if (isHull(line.id)) skin.push(wall);
    group.add(wall);
  }

  // Components sit on the deck inside the hull, except the ones that hang below.
  let standoff = 0;
  for (const component of graph.components) {
    standoff = Math.max(standoff, -boundsOf(component.type, landed).up.min);
  }

  for (const component of graph.components) {
    const outline = componentOutline(component);
    for (const point of outline) {
      radius = Math.max(radius, Math.hypot(point.x, point.y));
    }

    // Models are authored with their pivot on their base, so nearly everything
    // stands on the hull's floor. Thrusters are the exception — they fire
    // through the side, so they sit at mid-height.
    const spec = COMPONENT_SPECS[component.type];
    const base =
      spec.mountHeight === 'middle'
        ? 0
        : spec.mountHeight === 'raised'
          ? -SHIP_DEPTH / 2 + RAISED_HEIGHT * COMPONENT_SCALE
          : -SHIP_DEPTH / 2;
    const model = modelFor(component.type, landed);

    if (model) {
      void loadComponentGeometry(model).then((geometry) => {
        if (!geometry) return;
        const mesh = new THREE.Mesh(
          geometry,
          createStructureMaterial({ color: 0xc8d8e8, fadeDistance: 900 }),
        );
        mesh.position.set(component.position.x, component.position.y, base);
        // `renderFacing` is not simply the facing: a flip composed with a
        // rotation is not the rotation you started with, so a mirrored part
        // needs a different angle to end up pointing where its facing says.
        mesh.rotation.z = renderFacing(component);
        // The same factor the bounds were scaled by, so the shape you see is
        // the shape the editor tested.
        mesh.scale.setScalar(drawScaleOf(component.type));
        if (component.mirrored) mesh.scale.x *= -1;
        group.add(mesh);
      });
      continue;
    }

    // No model yet: draw the footprint as a low block, so the part is visibly
    // present and visibly a placeholder rather than silently missing.
    const shape = new THREE.Shape(outline.map((p) => new THREE.Vector2(p.x - component.position.x, p.y - component.position.y)));
    const block = new THREE.ExtrudeGeometry(shape, { depth: SHIP_DEPTH * 0.3, bevelEnabled: false });
    const mesh = new THREE.Mesh(
      withEdges(block),
      createStructureMaterial({ color: 0x7f8fa0, fadeDistance: 900 }),
    );
    mesh.position.set(component.position.x, component.position.y, base);
    group.add(mesh);
  }

  return {
    group,
    radius: Math.max(radius, 1),
    standoff,
    setSkinVisible: (visible: boolean) => {
      for (const piece of skin) piece.visible = visible;
    },
    dispose: () => {
      group.traverse((object) => {
        const mesh = object as Partial<THREE.Mesh>;
        // Model geometries are cached and shared across every ship on screen.
        if (mesh.geometry && !isSharedGeometry(mesh.geometry)) mesh.geometry.dispose();
        const material = mesh.material;
        if (Array.isArray(material)) material.forEach((m) => m.dispose());
        else material?.dispose();
      });
    },
  };
}
